import { ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { appearances, longDate } from "../format";
import { useStationOfChart } from "../hooks";
import { StationLogo } from "./StationLogo";
import type { ChartStat } from "../types";

const COLORS = ["var(--c1)", "var(--c2)", "var(--c3)", "var(--c4)", "var(--c5)", "var(--c6)"];
const PAGE = 60;
const weekday = new Intl.DateTimeFormat("pl-PL", { weekday: "long", timeZone: "UTC" });

type Sort = "new" | "old" | "best";
interface Row { slug: string; date: string; position: number }

/** "Gdzie grano": every chart the song was on, and under each tab every single edition, each one a link. */
export function Appearances({ history, charts, trackId }: {
  history: Record<string, { date: string; position: number }[]>;
  charts: ChartStat[];
  trackId: number;
}) {
  // Same order (and so the same colours) as the position chart.
  const slugs = useMemo(() => Object.keys(history).filter((s) => history[s]?.length), [history]);
  const [tab, setTab] = useState<string>("all");
  const [sort, setSort] = useState<Sort>("new");
  const [shown, setShown] = useState(PAGE);

  const stationOf = useStationOfChart();
  const info = (slug: string) => charts.find((c) => c.chart === slug);
  const color = (slug: string) => COLORS[Math.max(0, slugs.indexOf(slug)) % COLORS.length];

  const rows = useMemo(() => {
    const all: Row[] = (tab === "all" ? slugs : [tab]).flatMap((slug) => (history[slug] ?? []).map((p) => ({ slug, ...p })));
    const by: Record<Sort, (a: Row, b: Row) => number> = {
      new: (a, b) => b.date.localeCompare(a.date),
      old: (a, b) => a.date.localeCompare(b.date),
      best: (a, b) => a.position - b.position || b.date.localeCompare(a.date),
    };
    return all.sort(by[sort]);
  }, [history, slugs, tab, sort]);

  const select = (next: string) => { setTab(next); setShown(PAGE); };
  const current = tab === "all" ? null : info(tab);
  const total = slugs.reduce((n, s) => n + (history[s]?.length ?? 0), 0);
  const best = rows.reduce((m, r) => Math.min(m, r.position), Infinity);

  return (
    <section className="where" aria-label="Gdzie grano">
      <div className="where__tabs" role="tablist" aria-label="Lista">
        <button type="button" role="tab" id="tab-all" aria-selected={tab === "all"} aria-controls="where-panel" className="where__tab" onClick={() => select("all")}>
          Wszystkie <b>{total}</b>
        </button>
        {slugs.map((slug) => (
          <button key={slug} type="button" role="tab" id={`tab-${slug}`} aria-selected={tab === slug} aria-controls="where-panel" className="where__tab"
            style={{ "--tab": color(slug) } as React.CSSProperties} onClick={() => select(slug)}>
            {stationOf(slug) && <StationLogo slug={stationOf(slug)!} size={20} />}
            {info(slug)?.stationName ?? slug} <b>{history[slug]?.length}</b>
          </button>
        ))}
      </div>

      <div id="where-panel" role="tabpanel" aria-labelledby={`tab-${tab}`}>
        <div className="where__summary">
          <div>
            <h3 className="where__title">
              {current && stationOf(tab) && <StationLogo slug={stationOf(tab)!} size={28} />}
              {current ? `${current.stationName}: ${current.chartName}` : "Wszystkie listy"}
            </h3>
            <p className="muted small">
              {appearances(rows.length)} · najwyżej #{best}
              {current && (
                <> · {longDate(current.firstDate)}{current.firstDate !== current.lastDate && <> – {longDate(current.lastDate)}</>}</>
              )}
            </p>
          </div>
          <div className="where__tools">
            <label className="sr-only" htmlFor="where-sort">Sortuj notowania</label>
            <select id="where-sort" value={sort} onChange={(e) => { setSort(e.target.value as Sort); setShown(PAGE); }}>
              <option value="new">Najnowsze najpierw</option>
              <option value="old">Najstarsze najpierw</option>
              <option value="best">Najwyższe miejsca najpierw</option>
            </select>
          </div>
        </div>

        <ul className="where__list">
          {rows.slice(0, shown).map((r) => (
            <li key={`${r.slug}-${r.date}-${r.position}`}>
              <Link className="where__item" to={`/listy/${r.slug}/${r.date}?utwor=${trackId}`}
                aria-label={`${info(r.slug)?.chartName ?? r.slug}, ${longDate(r.date)}, miejsce ${r.position}`}>
                <span className="where__pos" data-top={r.position === 1 ? "1" : r.position <= 3 ? "3" : undefined}>#{r.position}</span>
                <span className="where__date">
                  {longDate(r.date)}
                  <small>
                    {tab === "all" ? `${info(r.slug)?.stationName ?? r.slug} · ` : ""}
                    {weekday.format(new Date(`${r.date}T00:00:00Z`))}
                  </small>
                </span>
                <ChevronRight className="where__go" size={18} aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
        {rows.length > shown && (
          <button type="button" className="btn where__more" onClick={() => setShown((n) => n + PAGE * 2)}>
            Pokaż więcej ({rows.length - shown})
          </button>
        )}
      </div>
    </section>
  );
}
